import type {
  ConversationRecord,
  MessageRecord,
  MessageReactionRecord,
} from "@jema-manager/shared";
import {
  ConversationContextType,
  ConversationScope,
  MessageDeliveryStatus,
  MessageType,
} from "@jema-manager/shared";
import { apiClient, type RequestConfig } from "./api-client";

type Role = "patient" | "doctor" | "admin" | "super_admin";
export interface UnreadCounts {
  unreadMessages: number;
  unreadConversations: number;
}

type V2ConversationParticipant = {
  id: string;
  userId: string;
  role: string;
  canReply: boolean;
  joinedAt: string;
  lastReadAt: string | null;
  displayName?: string;
  email?: string;
  avatarUrl?: string;
};

type V2ConversationSummary = {
  id: string;
  slug: string;
  caseId: string;
  organizationId: string | null;
  channelType: string;
  status: string;
  title: string | null;
  metadata?: Record<string, unknown>;
  createdByUserId: string | null;
  participants: V2ConversationParticipant[];
  unreadCount: number;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  createdAt: string;
  updatedAt: string;
};

type V2Receipt = {
  id: string;
  userId: string;
  deliveredAt: string | null;
  readAt: string | null;
};

type V2Reaction = {
  id: string;
  messageId: string;
  userId: string;
  emoji: string;
  createdAt: string;
};

type V2Message = {
  id: string;
  conversationId: string;
  senderUserId: string | null;
  senderName: string | null;
  body: string;
  messageType: string;
  attachments: unknown[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  receipts: V2Receipt[];
  reactions: V2Reaction[];
};

const channelTypeToScope = (channelType: string): ConversationScope => {
  switch (channelType) {
    case "patient_admin":
      return ConversationScope.PATIENT_ADMIN;
    case "doctor_admin":
      return ConversationScope.DOCTOR_ADMIN;
    case "patient_doctor":
      return ConversationScope.PATIENT_DOCTOR;
    default:
      return ConversationScope.INTERNAL;
  }
};

const deriveContextType = (
  metadata: Record<string, unknown> | undefined,
): ConversationContextType => {
  if (typeof metadata?.quote_id === "string") {
    return ConversationContextType.QUOTE;
  }
  if (typeof metadata?.service_request_id === "string") {
    return ConversationContextType.SERVICE_REQUEST;
  }
  return ConversationContextType.SUPPORT;
};

const mapConversation = (conversation: V2ConversationSummary): ConversationRecord & {
  unread_count?: number;
  last_message_preview?: string;
  participants?: ConversationRecord["metadata"] extends never ? never : Array<{
    id: string;
    userId: string;
    role: string;
    canReply: boolean;
    joinedAt: string;
    lastReadAt?: string;
    displayName?: string;
    email?: string;
    avatarUrl?: string;
  }>;
} => {
  const metadata = conversation.metadata ?? {};
  const contextId =
    typeof metadata.quote_id === "string"
      ? metadata.quote_id
      : typeof metadata.service_request_id === "string"
        ? metadata.service_request_id
        : undefined;

  return {
    id: conversation.id,
    slug: conversation.slug || conversation.id,
    title: conversation.title ?? undefined,
    contextType: deriveContextType(metadata),
    contextId,
    scope: channelTypeToScope(conversation.channelType),
    createdBy: conversation.createdByUserId ?? "",
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt,
    lastMessageAt: conversation.lastMessageAt ?? undefined,
    isArchived: conversation.status === "archived",
    metadata: {
      ...(typeof metadata.quote_id === "string" ? { quoteId: metadata.quote_id } : {}),
      ...(typeof metadata.service_request_id === "string"
        ? { serviceRequestId: metadata.service_request_id }
        : {}),
    },
    unread_count: conversation.unreadCount,
    last_message_preview: conversation.lastMessagePreview ?? undefined,
    participants: (conversation.participants ?? []).map((participant) => ({
      id: participant.id,
      userId: participant.userId,
      role: participant.role,
      canReply: participant.canReply,
      joinedAt: participant.joinedAt,
      ...(participant.lastReadAt ? { lastReadAt: participant.lastReadAt } : {}),
      ...(participant.displayName ? { displayName: participant.displayName } : {}),
      ...(participant.email ? { email: participant.email } : {}),
      ...(participant.avatarUrl ? { avatarUrl: participant.avatarUrl } : {}),
    })),
  };
};

const mapMessageStatus = (
  message: V2Message,
): MessageDeliveryStatus => {
  const receipts = message.receipts ?? [];
  if (receipts.some((receipt) => receipt.readAt)) {
    return MessageDeliveryStatus.READ;
  }
  if (receipts.some((receipt) => receipt.deliveredAt)) {
    return MessageDeliveryStatus.DELIVERED;
  }
  return MessageDeliveryStatus.SENT;
};

const mapMessage = (message: V2Message): MessageRecord => ({
  id: message.id,
  conversationId: message.conversationId,
  senderId: message.senderUserId ?? "",
  ...(message.senderName ? { senderName: message.senderName } : {}),
  body: message.body,
  type: message.messageType === "system" ? MessageType.SYSTEM : MessageType.TEXT,
  attachments: Array.isArray(message.attachments) ? (message.attachments as any[]) : [],
  metadata: message.metadata ?? {},
  status: mapMessageStatus(message),
  sentAt: message.createdAt,
  updatedAt: message.updatedAt,
  ...(message.deletedAt ? { deletedAt: message.deletedAt } : {}),
  receipts: (message.receipts ?? []).map((receipt, index) => ({
    id: Number.parseInt(receipt.id, 10) || index + 1,
    messageId: message.id,
    recipientId: receipt.userId,
    status: receipt.readAt
      ? MessageDeliveryStatus.READ
      : receipt.deliveredAt
        ? MessageDeliveryStatus.DELIVERED
        : MessageDeliveryStatus.SENT,
    ...(receipt.deliveredAt ? { deliveredAt: receipt.deliveredAt } : {}),
    ...(receipt.readAt ? { readAt: receipt.readAt } : {}),
  })),
  reactions: (message.reactions ?? []).map((reaction, index) => ({
    id: Number.parseInt(reaction.id, 10) || index + 1,
    messageId: reaction.messageId,
    userId: reaction.userId,
    emoji: reaction.emoji,
    createdAt: reaction.createdAt,
  })),
});

const basePathForRole = (role: Role) => {
  if (role === "patient") return "/patients/messages";
  if (role === "doctor") return "/doctors/messages";
  return "/admin/messages";
};

export const fetchConversations = async (
  role: Role,
  config?: Omit<RequestConfig, 'method' | 'body'>
): Promise<ConversationRecord[]> => {
  const path = basePathForRole(role);
  const response = await apiClient.get<{ data: V2ConversationSummary[] }>(
    path,
    undefined,
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:conversations',
      ...config,
    }
  );
  const payload = response.data as { data: V2ConversationSummary[] } | V2ConversationSummary[];
  const list = Array.isArray(payload) ? payload : payload.data ?? [];
  return list.map(mapConversation);
};

export const fetchUnreadCounts = async (
  role: Role,
  config?: Omit<RequestConfig, 'method' | 'body'>
): Promise<UnreadCounts> => {
  const base = basePathForRole(role);
  const response = await apiClient.get<{ data: UnreadCounts } | UnreadCounts>(
    `${base}/unread-count`,
    undefined,
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:unread-count',
      ...config,
    }
  );
  const payload = response as { data?: UnreadCounts } | UnreadCounts;
  if ('unreadMessages' in payload && 'unreadConversations' in payload) {
    return payload;
  }
  return payload.data ?? { unreadMessages: 0, unreadConversations: 0 };
};

export const fetchConversationForQuote = async (
  role: Role,
  quoteId: string,
  config?: Omit<RequestConfig, 'method' | 'body'>
) => {
  const base = basePathForRole(role);
  const response = await apiClient.get<{ data: V2ConversationSummary }>(
    `${base}/quotes/${quoteId}`,
    undefined,
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:conversation-for-quote',
      ...config,
    }
  );
  return mapConversation(response.data);
};

export const fetchConversationForServiceRequest = async (
  role: Role,
  serviceRequestId: string,
  scope: "patient_admin" | "doctor_admin",
  config?: Omit<RequestConfig, 'method' | 'body'>
) => {
  const base = basePathForRole(role);
  const response = await apiClient.get<{ data: V2ConversationSummary }>(
    `${base}/service-requests/${serviceRequestId}/${scope}`,
    undefined,
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:conversation-for-service-request',
      ...config,
    }
  );
  return mapConversation(response.data);
};

export const fetchMessages = async (
  role: Role,
  roomSlug: string,
  params?: { limit?: number; before?: string },
  config?: Omit<RequestConfig, 'method' | 'body'>
) => {
  const base = basePathForRole(role);
  const query = new URLSearchParams();
  query.set("limit", String(params?.limit ?? 20));
  if (params?.before) query.set("before", params.before);
  const response = await apiClient.get<{ data: { conversation: V2ConversationSummary; messages: V2Message[] } }>(
    `${base}/${roomSlug}/messages${query.toString() ? `?${query.toString()}` : ""}`,
    undefined,
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:thread',
      ...config,
    }
  );
  return {
    conversation: mapConversation(response.data.conversation),
    messages: (response.data.messages ?? []).map(mapMessage),
  };
};

export const sendMessage = async (role: Role, roomSlug: string, body: string): Promise<MessageRecord> => {
  const base = basePathForRole(role);
  const response = await apiClient.post<{ data: V2Message }>(
    `${base}/${roomSlug}/messages`,
    { body },
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:send-message',
    },
  );
  return mapMessage(response.data);
};

/**
 * Mark conversation as read via the API so read-state updates stay server-owned.
 */
export const markRead = async (role: Role, roomSlug: string): Promise<{ conversationId: string; readAt: string }> => {
  const base = basePathForRole(role);
  const response = await apiClient.post<{ data: { conversationId: string; readAt: string } }>(
    `${base}/${roomSlug}/read`,
    {},
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:mark-read',
    }
  );
  return response.data;
};

export const addReaction = async (
  role: Role,
  roomSlug: string,
  messageId: string,
  emoji: string,
): Promise<MessageReactionRecord> => {
  const base = basePathForRole(role);
  const response = await apiClient.post<{ data: MessageReactionRecord }>(
    `${base}/${roomSlug}/reactions`,
    { messageId, emoji },
    {
      timeoutProfile: 'messaging',
      requestLabel: 'messaging:add-reaction',
    },
  );
  return response.data;
};

export const removeReaction = async (
  role: Role,
  roomSlug: string,
  messageId: string,
  emoji: string,
): Promise<void> => {
  const base = basePathForRole(role);
  await apiClient.delete(`${base}/${roomSlug}/reactions?messageId=${encodeURIComponent(messageId)}&emoji=${encodeURIComponent(emoji)}`, {
    timeoutProfile: 'messaging',
    requestLabel: 'messaging:remove-reaction',
  });
};
